import type { FastRow, FastSource } from "./types";

const surname = (name: string) => name.trim().split(/\s+/).pop() ?? name;

/** Records from Paperclip's sources that are not papers, by id: where each one lives. */
const RECORDS: [RegExp, (id: string) => string, string][] = [
  [/^NCT\d{8}$/, (id) => `https://clinicaltrials.gov/study/${id}`, "ClinicalTrials.gov"],
  [/^(ISRCTN\d+|\d{4}-\d{6}-\d{2}(-\d{2})?|jRCT\w+|UMIN\d+|ChiCTR[\w-]+|ACTRN\d+|CTRI\/[\d/]+|DRKS\d+)$/,
    (id) => `https://trialsearch.who.int/Trial2.aspx?TrialID=${encodeURIComponent(id)}`, "WHO trial registry search"],
  [/^arx_/, (id) => `https://arxiv.org/abs/${id.slice(4)}`, "arXiv"],
  [/^[A-Z]{2}-\d+-[A-Z]\d?$/, (id) => `https://patents.google.com/patent/${id.replace(/-/g, "")}`, "Google Patents"],
  [/^G(SE|DS)\d+$/, (id) => `https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=${id}`, "NCBI GEO"],
  [/^VCV\d+$/, (id) => `https://www.ncbi.nlm.nih.gov/clinvar/variation/${Number(id.slice(3))}/`, "ClinVar"],
  [/^ENSG\d+-/, (id) => `https://platform.opentargets.org/evidence/${id.split("-")[0]}/${id.split("-").slice(1).join("-")}`, "Open Targets"],
  [/^CHEMBL\d+-/, (id) => `https://platform.opentargets.org/drug/${id.split("-")[0]}`, "Open Targets"],
  [/^([OPQ]\d[A-Z\d]{3}\d|[A-NR-Z]\d([A-Z][A-Z\d]{2}\d){1,2})$/, (id) => `https://www.uniprot.org/uniprotkb/${id}`, "UniProt"],
];

const record = (source: FastSource) => {
  const hit = RECORDS.find(([re]) => re.test(source.doc_id));
  if (source.url) return { href: source.url, label: hit?.[2] ?? source.journal ?? "the source" };
  return hit ? { href: hit[1](source.doc_id), label: hit[2] } : null;
};

/** A trial registry record, a database entry or a preprint server page rather than a paper in Paperclip's reader. */
export const isRegistry = (source: FastSource) => record(source) !== null;

/** "Gu et al. 2024", "Gu & Wu 2024" or "Gu 2024"; the title when the authors are not known. */
export const citeSource = (source: FastSource) => {
  if (isRegistry(source) && !source.doc_id.startsWith("arx_")) {
    const acronym = (source.aliases ?? []).find((a) => a !== source.doc_id);
    return acronym ? `${acronym} · ${source.doc_id}` : source.doc_id;
  }
  const authors = source.authors ?? [];
  if (!authors.length) return null;
  const who =
    authors.length === 1
      ? surname(authors[0])
      : authors.length === 2
        ? `${surname(authors[0])} & ${surname(authors[1])}`
        : `${surname(authors[0])} et al.`;
  return source.year ? `${who} ${source.year}` : who;
};

/** The source's own page: its registry or database for records; PubMed, PMC, the DOI or the preprint server for papers. */
export const originalLink = (source: FastSource): { href: string; label: string } | null => {
  const rec = record(source);
  if (rec) return rec;
  if (source.pmid) return { href: `https://pubmed.ncbi.nlm.nih.gov/${source.pmid}/`, label: "PubMed" };
  if (/^PMC\d+$/i.test(source.doc_id))
    return { href: `https://www.ncbi.nlm.nih.gov/pmc/articles/${source.doc_id.toUpperCase()}/`, label: "PubMed Central" };
  const server = source.doc_id.startsWith("bio_") ? "bioRxiv" : source.doc_id.startsWith("med_") ? "medRxiv" : null;
  if (source.doi) return { href: `https://doi.org/${source.doi}`, label: server ?? "the publisher" };
  return null;
};

/** Where the source lives: its original page, or the Paperclip reader when none is known. */
export const sourceLink = (source: FastSource) =>
  originalLink(source) ?? { href: `/citations/papers/${encodeURIComponent(source.doc_id)}`, label: "Paperclip" };

/** The paper's citation, or its title when the authors are not known. */
export const paperName = (row: FastRow) => citeSource(row.source) ?? row.source.title ?? row.row_ref;

const DESIGN: Record<string, string> = {
  rct: "RCT",
  meta_analysis: "Meta-analysis",
  systematic_review: "Systematic review",
  case_control: "Case-control",
  cross_sectional: "Cross-sectional",
  case_report: "Case report",
  case_series: "Case series",
  non_randomized: "Non-randomized trial",
};

export const designName = (design: string | null | undefined) => {
  if (!design || design === "other" || design === "unknown") return null;
  const name = DESIGN[design] ?? design.replace(/_/g, " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const citationsText = (n: number | null | undefined) =>
  n == null ? null : `${n.toLocaleString()} ${n === 1 ? "citation" : "citations"}`;
